import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { secureFetch } from './onboardingSlice';
import { baseOnboardingUrl } from './hooks';

const baseUrl = baseOnboardingUrl;

// AsyncThunk for fetching existing practices
export const fetchExistingPractices = createAsyncThunk(
  'createOnboarding/fetchExistingPractices',
  async (organizationId) => {
    const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/get_other_practices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        organizationId: organizationId
      })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch existing practices');
    }

    const data = await response.json();
    return data.practiceIds || [];
  }
);

// AsyncThunk for creating new onboarding
export const createNewOnboarding = createAsyncThunk(
  'createOnboarding/createNewOnboarding',
  async ({ newOnboarding, isSubsequentDSO, organizationId, manualPracticeId }, { rejectWithValue }) => {
    try {
      // Validate required fields for DSO practice
      if (isSubsequentDSO) {
        if (!organizationId.trim()) {
          return rejectWithValue('Organization ID is required for DSO practice');
        }
        if (!manualPracticeId.trim()) {
          return rejectWithValue('Practice ID is required for DSO practice');
        }
      }

      // Include the DSO practice fields in the request
      const onboardingData = {
        ...newOnboarding,
        isSubsequentDSO,
        organizationId: isSubsequentDSO ? organizationId : '',
        practiceId: isSubsequentDSO ? manualPracticeId.trim() : null
      };

      console.log("Creating onboarding with data: ", onboardingData);

      const response = await secureFetch(`${baseUrl}/api/onboardings_view/general/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(onboardingData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create onboarding');
      }

      const result = await response.json();
      console.log("Create onboarding response: ", result);
      return result;
    } catch (error) {
      console.error('Error creating onboarding:', error);
      return rejectWithValue(error.message || 'Failed to create onboarding');
    }
  }
);

const initialState = {
  // Form state
  newOnboarding: {
    name: '',
    signedDate: '',
    kickoff: ''
  },
  isSubsequentDSO: false,
  organizationId: '',
  manualPracticeId: '',
  existingPracticeIds: [],
  
  // Loading and error states
  loading: false,
  loadingPractices: false,
  error: null,
  
  // Success state
  success: false
};

const createOnboardingSlice = createSlice({
  name: 'createOnboarding',
  initialState,
  reducers: {
    setNewOnboarding: (state, action) => {
      state.newOnboarding = { ...state.newOnboarding, ...action.payload };
    },
    setIsSubsequentDSO: (state, action) => {
      state.isSubsequentDSO = action.payload;
      // Reset DSO-related fields when toggling
      if (!action.payload) {
        state.organizationId = '';
        state.manualPracticeId = '';
        state.existingPracticeIds = [];
      }
    },
    setOrganizationId: (state, action) => {
      state.organizationId = action.payload;
      // Clear existing practices when organization ID changes
      if (state.existingPracticeIds.length > 0) {
        state.existingPracticeIds = [];
      }
    },
    setManualPracticeId: (state, action) => {
      state.manualPracticeId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = false;
    },
    resetForm: (state) => {
      state.newOnboarding = {
        name: '',
        signedDate: '',
        kickoff: ''
      };
      state.isSubsequentDSO = false;
      state.organizationId = '';
      state.manualPracticeId = '';
      state.existingPracticeIds = [];
      state.error = null;
      state.success = false;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchExistingPractices.pending, (state) => {
        state.loadingPractices = true;
        state.error = null;
      })
      .addCase(fetchExistingPractices.fulfilled, (state, action) => {
        state.loadingPractices = false;
        state.existingPracticeIds = action.payload;
      })
      .addCase(fetchExistingPractices.rejected, (state, action) => {
        state.loadingPractices = false;
        state.error = action.error.message;
        state.existingPracticeIds = [];
      })
      .addCase(createNewOnboarding.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createNewOnboarding.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        // Reset form after successful creation
        state.newOnboarding = {
          name: '',
          signedDate: '',
          kickoff: ''
        };
        state.isSubsequentDSO = false;
        state.organizationId = '';
        state.manualPracticeId = '';
        state.existingPracticeIds = [];
      })
      .addCase(createNewOnboarding.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.success = false;
      });
  }
});

export const {
  setNewOnboarding,
  setIsSubsequentDSO,
  setOrganizationId,
  setManualPracticeId,
  clearError,
  clearSuccess,
  resetForm
} = createOnboardingSlice.actions;

// Selectors
export const selectNewOnboarding = (state) => state.createOnboarding.newOnboarding;
export const selectIsSubsequentDSO = (state) => state.createOnboarding.isSubsequentDSO;
export const selectOrganizationId = (state) => state.createOnboarding.organizationId;
export const selectManualPracticeId = (state) => state.createOnboarding.manualPracticeId;
export const selectExistingPracticeIds = (state) => state.createOnboarding.existingPracticeIds;
export const selectLoading = (state) => state.createOnboarding.loading;
export const selectLoadingPractices = (state) => state.createOnboarding.loadingPractices;
export const selectError = (state) => state.createOnboarding.error;
export const selectSuccess = (state) => state.createOnboarding.success;

// Computed selectors
export const selectIsOrganizationIdValid = (state) => {
  const organizationId = state.createOnboarding.organizationId;
  return organizationId && /^org-\d+$/.test(organizationId);
};

export const selectSortedAndHighlightedPracticeIds = (state) => {
  const existingPracticeIds = state.createOnboarding.existingPracticeIds;
  
  if (!existingPracticeIds.length) return [];
  
  // Extract numbers from practice IDs and sort them
  const practiceNumbers = existingPracticeIds
    .map(id => {
      const match = id.match(/practice-(\d+)/);
      return match ? parseInt(match[1]) : null;
    })
    .filter(num => num !== null)
    .sort((a, b) => a - b);
  
  // Find contiguous spans
  const spans = [];
  let currentSpan = [practiceNumbers[0]];
  
  for (let i = 1; i < practiceNumbers.length; i++) {
    if (practiceNumbers[i] === practiceNumbers[i-1] + 1) {
      currentSpan.push(practiceNumbers[i]);
    } else {
      if (currentSpan.length > 1) {
        spans.push([...currentSpan]);
      }
      currentSpan = [practiceNumbers[i]];
    }
  }
  
  if (currentSpan.length > 1) {
    spans.push(currentSpan);
  }
  
  // Create result with highlighting info
  return practiceNumbers.map(num => ({
    number: num,
    practiceId: `practice-${num}`,
    isContiguous: spans.some(span => span.includes(num))
  }));
};

export default createOnboardingSlice.reducer;
